import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import blogList from '../data/blog/blog-list.json';
import { photoAlbums } from '../data/photoAlbums';
import authorizedUsers from '../data/users.json';
import { isSignedIn, getUserProfile } from '../utils/googleAuth';

// Create a context for all blog images
const blogImages = require.context('../data/blog', true, /\.(png|jpe?g|svg|gif)$/);

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Derived state from URL
  const filter = {
    textVal: searchParams.get('search') || '',
    year: searchParams.get('year') ? Number(searchParams.get('year')) : null,
    tag: searchParams.get('tag') || null,
    city: searchParams.get('city') || null,
    country: searchParams.get('country') || null,
    continent: searchParams.get('continent') || null
  };
  const selectedPage = searchParams.get('page') ? Math.max(0, Number(searchParams.get('page')) - 1) : 0;

  const [pagedResults, setPagedResults] = useState([]);
  const [pages, setPages] = useState([]);
  const [years, setYears] = useState([]);
  const [tags, setTags] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [continents, setContinents] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);

  const handleRandomPost = () => {
    const activePosts = blogList.filter(p => p.active !== false);
    if (activePosts.length > 0) {
      const randomIndex = Math.floor(Math.random() * activePosts.length);
      const randomPost = activePosts[randomIndex];
      navigate(`/blog/${randomPost.id}`);
    }
  };

  const getYear = (dateString) => Number(new Date(dateString).getFullYear());

  // Check if user is authorized
  const checkUserAuthorization = (userProfile) => {
    if (!userProfile || !userProfile.email) {
      return false;
    }

    return authorizedUsers.includes(userProfile.email.toLowerCase());
  };

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const signedIn = isSignedIn();
      setIsAuthenticated(signedIn);

      if (signedIn) {
        const profile = getUserProfile();
        setIsAuthorized(checkUserAuthorization(profile));
      } else {
        setIsAuthorized(false);
      }
    };

    checkAuth();

    // Listen for auth state changes
    const handleAuthChange = (signedIn, userInfo) => {
      setIsAuthenticated(signedIn);
      setIsAuthorized(signedIn ? checkUserAuthorization(userInfo) : false);
    };

    window.addEventListener('googleSignIn', (event) => {
      handleAuthChange(true, event.detail);
    });

    window.addEventListener('googleSignOut', () => {
      handleAuthChange(false, null);
    });

    return () => {
      window.removeEventListener('googleSignIn', handleAuthChange);
      window.removeEventListener('googleSignOut', handleAuthChange);
    };
  }, []);

  // Helper to update params while preserving others
  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams);
  };

  const search = useCallback(() => {
    let filtered = blogList.filter(post => {
      if (post.active === false) {
        return false;
      }
      if (filter.textVal && !post.title.toLowerCase().includes(filter.textVal.toLowerCase())) {
        return false;
      }
      if (filter.year && filter.year !== getYear(post.date)) {
        return false;
      }
      if (filter.tag && !(post.tags || []).includes(filter.tag)) {
        return false;
      }
      if (filter.city && !(post.cities || []).includes(filter.city)) {
        return false;
      }
      if (filter.country && !(post.countries || []).includes(filter.country)) {
        return false;
      }
      if (filter.continent && !(post.continents || []).includes(filter.continent)) {
        return false;
      }
      return true;
    });

    const counts = {
      years: {},
      tags: {},
      cities: {},
      countries: {},
      continents: {}
    };

    const addToCounts = (key, value) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach(v => {
          counts[key][v] = (counts[key][v] || 0) + 1;
        });
      } else {
        counts[key][value] = (counts[key][value] || 0) + 1;
      }
    };

    filtered.forEach(post => {
      addToCounts('years', getYear(post.date));
      addToCounts('tags', post.tags);
      addToCounts('cities', post.cities);
      addToCounts('countries', post.countries);
      addToCounts('continents', post.continents);
    });

    const formatCounts = (obj) => Object.entries(obj)
      .map(([value, count]) => {
        const numValue = Number(value);
        const isNumeric = !isNaN(numValue) && value.trim() !== "" && /^\d+$/.test(value);
        return { value: isNumeric ? numValue : value, count };
      })
      .sort((a, b) => {
        if (typeof a.value === 'number' && typeof b.value === 'number') {
          return b.value - a.value;
        }
        return String(a.value).localeCompare(String(b.value));
      });

    setYears(formatCounts(counts.years));
    setTags(formatCounts(counts.tags).filter(t => t.count > 1 || t.value === filter.tag));
    setCities(formatCounts(counts.cities));
    setCountries(formatCounts(counts.countries));
    setContinents(formatCounts(counts.continents));

    const itemsPerPage = 12;
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const pageArray = [];
    for (let i = 0; i < totalPages; i++) {
      pageArray.push(i);
    }
    setPages(pageArray);

    const startIndex = selectedPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPagedResults(filtered.slice(startIndex, endIndex));
  }, [filter.textVal, filter.year, filter.tag, filter.city, filter.country, filter.continent, selectedPage]);

  const toggleFilter = (type, val) => {
    const current = filter[type];
    updateParams({ [type]: current === val ? null : val, page: 1 });
  };

  const setSelectedPageHandler = (page) => {
    updateParams({ page: page + 1 });
  };

  useEffect(() => {
    search();
  }, [search]);

  // Helper to load thumbnail
  const getThumbnailSrc = (post) => {
    if (!post.thumb) return null;
    try {
      const relativePath = `./${post.path}/${post.thumb}`;
      return blogImages(relativePath);
    } catch (e) {
      console.warn(`Thumbnail not found for post ${post.id}`, e);
      return null;
    }
  };

  // Show authorization message if not authenticated or not authorized
  if (!isAuthenticated || !isAuthorized) {
    return (
      <section id="about">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center">
              <h2>Blog</h2>
              <hr className="star-primary" />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-8 col-lg-offset-2">
              <div className="text-center" style={{
                padding: '40px 20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '10px',
                border: '2px solid #e9ecef',
                margin: '20px 0'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>
                  <i className="fa fa-fw fa-lock" style={{ color: '#6c757d' }}></i>
                </div>
                <p style={{
                  fontSize: '18px',
                  color: '#6c757d',
                  marginBottom: '30px',
                  lineHeight: '1.6'
                }}>
                  These pages are for friends and family only.
                </p>
                {!isAuthenticated && (
                  <p style={{
                    fontSize: '14px',
                    color: '#868e96',
                    marginBottom: '20px'
                  }}>
                    Please sign in with an authorized account to access the blog.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show blog content if authenticated and authorized
  return (
    <section id="blog">
      <div className="container">
        <div className="row">
          <div className="col-lg-12 text-center">
            <h2>Blog</h2>
            <hr className="star-primary" />

            <button
              className="btn btn-success"
              onClick={handleRandomPost}
              style={{ marginBottom: '20px' }}
            >
              <i className="fa fa-random"></i> Go to random post!
            </button>
          </div>
        </div>

        <div className="row divider">
          <div className="col-xs-12" style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span>Filter by:</span>
              <input
                className="form-control"
                value={filter.textVal}
                onChange={(e) => updateParams({ search: e.target.value, page: 1 })}
                placeholder="Search titles..."
                style={{ width: '200px', display: 'inline-block' }}
              />
              <div
                className="filter-label"
                onClick={() => updateParams({ search: '', page: 1 })}
                style={{ display: filter.textVal ? 'inline-block' : 'none', cursor: 'pointer' }}
              >
                Clear Search X
              </div>
            </div>
          </div>

          {[
            { id: 'year', label: 'Year', items: years, field: 'year' },
            { id: 'tag', label: 'Tags', items: tags, field: 'tag' },
            { id: 'city', label: 'Cities', items: cities, field: 'city' },
            { id: 'country', label: 'Countries', items: countries, field: 'country' },
            { id: 'continent', label: 'Continents', items: continents, field: 'continent' }
          ].map((section) => (
            <div key={section.id} className="col-xs-12" style={{ marginBottom: '10px' }}>
              <div
                onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                style={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '5px 0',
                  borderBottom: '1px solid #eee'
                }}
              >
                <i className={`fa fa-fw ${expandedSection === section.id ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                {section.label}
                {filter[section.field] && (
                  <span className={`label badge-${section.id}`} style={{ marginLeft: '10px' }}>
                    {filter[section.field]}
                  </span>
                )}
              </div>

              {expandedSection === section.id && (
                <div style={{ marginTop: '10px', paddingLeft: '20px' }}>
                  {section.items.length > 0 ? (
                    section.items.map((item, index) => (
                      <div
                        key={index}
                        className={`filter-label ${filter[section.field] === item.value ? 'filter-label-sel' : ''} ${section.field === 'city' ? 'filter-city' :
                          section.field === 'country' ? 'filter-country' :
                            section.field === 'continent' ? 'filter-continent' : ''
                          }`}
                        onClick={() => toggleFilter(section.field, item.value)}
                        style={{ marginBottom: '5px' }}
                      >
                        {item.value}&nbsp;<span style={{ fontStyle: 'italic' }}>[{item.count}]</span>
                        {filter[section.field] === item.value && <span> X</span>}
                      </div>
                    ))
                  ) : (
                    <div style={{ fontStyle: 'italic', color: '#999', fontSize: '13px' }}>No items available</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="row divider">
          {pagedResults.length > 0 ? (
            <div className="blog-posts-grid">
              {pagedResults.map((post) => {
                let thumbSrc = null;
                if (post.thumb) {
                  if (post.thumb.startsWith('http')) {
                    thumbSrc = post.thumb;
                  } else {
                    try {
                      // Use the webpack context to require the image
                      thumbSrc = blogImages(`./${post.path}/${post.thumb}`);
                    } catch (e) {
                      console.error(`Thumbnail not found for post: ${post.title}`, e);
                    }
                  }
                }

                return (
                  <div key={post.id} className="palb-box">
                    <div className="palb-header">
                      <Link to={`/blog/${post.id}`} className="palb-title">
                        {post.title}
                      </Link>
                      &nbsp;
                      <span className="palb-date">{new Date(post.date).toLocaleDateString()}</span>
                    </div>

                    <div className="palb-labels">
                      {(post.tags || []).map((tag, index) => (
                        <div
                          key={index}
                          className="palb-label filter-label"
                          onClick={() => toggleFilter('tag', tag)}
                        >
                          {tag}
                        </div>
                      ))}
                      {(post.cities || []).map((city, index) => (
                        <div
                          key={`city-${index}`}
                          className={`palb-label filter-label filter-city ${filter.city === city ? 'filter-label-sel' : ''}`}
                          onClick={() => toggleFilter('city', city)}
                        >
                          {city}
                        </div>
                      ))}
                      {(post.countries || []).map((country, index) => (
                        <div
                          key={`country-${index}`}
                          className={`palb-label filter-label filter-country ${filter.country === country ? 'filter-label-sel' : ''}`}
                          onClick={() => toggleFilter('country', country)}
                        >
                          {country}
                        </div>
                      ))}
                      {(post.continents || []).map((continent, index) => (
                        <div
                          key={`continent-${index}`}
                          className={`palb-label filter-label filter-continent ${filter.continent === continent ? 'filter-label-sel' : ''}`}
                          onClick={() => toggleFilter('continent', continent)}
                        >
                          {continent}
                        </div>
                      ))}
                    </div>

                    {thumbSrc && (
                      <div className="palb-media">
                        <Link to={`/blog/${post.id}`}>
                          <img
                            className="palb-img"
                            src={thumbSrc}
                            alt={post.title}
                          />
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="col-xs-12 text-center">
              <p style={{ fontSize: '18px', color: '#6c757d', marginTop: '40px' }}>
                No blog posts found matching your criteria.
              </p>
            </div>
          )}
        </div>

        <div className="col-xs-12" style={{ textAlign: 'center' }}>
          <div
            className="filter-label filter-label-paging-btn"
            onClick={() => setSelectedPageHandler(selectedPage - 1)}
            style={{ display: selectedPage > 0 ? 'inline-block' : 'none' }}
          >
            &lt;
          </div>

          {pages.map((page) => (
            <div
              key={page}
              className={`filter-label filter-label-paging-btn ${selectedPage === page ? 'filter-label-sel' : ''}`}
              onClick={() => setSelectedPageHandler(page)}
            >
              {page + 1}
            </div>
          ))}

          <div
            className="filter-label filter-label-paging-btn"
            onClick={() => setSelectedPageHandler(selectedPage + 1)}
            style={{ display: selectedPage < pages.length - 1 ? 'inline-block' : 'none' }}
          >
            &gt;
          </div>
        </div>
      </div>
    </section >
  );
};

export default Blog;
